import { useState } from 'react';
import type { ReactNode } from 'react';
import { emitCoreActivity } from '@/lib/coreEventHooks';
import type { CoreContact, CoreCustomer, CoreOrganization, CoreProfessional } from '@/lib/zifekCore';
import {
  createCoreContact,
  createCoreCustomer,
  createCoreProfessional,
  updateCoreContact,
  updateCoreCustomer,
  updateCoreProfessional,
  setCorePersonStatus,
  type CorePersonTable,
} from '@/lib/zifekCore';
import Modal from './Modal';

interface Props {
  org: CoreOrganization;
  contacts: CoreContact[];
  customers: CoreCustomer[];
  professionals: CoreProfessional[];
  onChanged: () => void;
  /** Ids des fiches client reliées au carnet de crédit. */
  creditLinked?: number[];
  /** Nombre de commandes par fiche client. */
  orderCounts?: Record<number, number>;
  /** Ids des fiches contact reliées à un fournisseur (Finance). */
  supplierLinked?: number[];
  /** Ids des fiches contact reliées à un partenaire (boutique). */
  partnerLinked?: number[];
}

type SubTab = 'contacts' | 'customers' | 'professionals';
type StatusFilter = 'all' | 'active' | 'inactive';

const INPUT = 'w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors';

const ROLE_OPTIONS = [
  { value: 'client', label: 'Client' },
  { value: 'prospect', label: 'Prospect' },
  { value: 'supplier', label: 'Fournisseur' },
  { value: 'partner', label: 'Partenaire' },
  { value: 'employee', label: 'Employé' },
  { value: 'professional', label: 'Professionnel' },
  { value: 'representative', label: 'Représentant' },
  { value: 'other', label: 'Autre' },
];

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-foreground-600 mb-1">{label}</span>
      {children}
    </label>
  );
}

function EmptyState({ icon, text }: { icon: string; text: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 bg-background-50 border border-background-200/70 rounded-lg">
      <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3">
        <i className={`${icon} text-2xl text-foreground-400`}></i>
      </div>
      <p className="text-foreground-600">{text}</p>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const active = status !== 'inactive';
  return (
    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${active ? 'bg-accent-100 text-accent-900' : 'bg-foreground-100 text-foreground-600'}`}>
      {active ? 'Actif' : 'Désactivé'}
    </span>
  );
}

export default function PeopleManager({ org, contacts, customers, professionals, onChanged, creditLinked = [], orderCounts = {}, supplierLinked = [], partnerLinked = [] }: Props) {
  const [sub, setSub] = useState<SubTab>('contacts');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [error, setError] = useState('');

  const creditSet = new Set(creditLinked);
  const supplierSet = new Set(supplierLinked);
  const partnerSet = new Set(partnerLinked);

  // contacts form
  const [contactType, setContactType] = useState('person');
  const [roleType, setRoleType] = useState('client');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [notes, setNotes] = useState('');

  // customer form
  const [custName, setCustName] = useState('');
  const [custType, setCustType] = useState('individual');
  const [custCompany, setCustCompany] = useState('');
  const [custEmail, setCustEmail] = useState('');
  const [custPhone, setCustPhone] = useState('');
  const [custNotes, setCustNotes] = useState('');

  // professional form
  const [proName, setProName] = useState('');
  const [profession, setProfession] = useState('');
  const [specialties, setSpecialties] = useState('');
  const [languages, setLanguages] = useState('');
  const [experienceYears, setExperienceYears] = useState('');
  const [verification, setVerification] = useState('unverified');
  const [publicProfile, setPublicProfile] = useState(false);
  const [bio, setBio] = useState('');
  const [certifications, setCertifications] = useState('');

  const reset = () => {
    setContactType('person'); setRoleType('client'); setFirstName(''); setLastName(''); setCompanyName(''); setJobTitle(''); setEmail(''); setPhone(''); setWhatsapp(''); setNotes('');
    setCustName(''); setCustType('individual'); setCustCompany(''); setCustEmail(''); setCustPhone(''); setCustNotes('');
    setProName(''); setProfession(''); setSpecialties(''); setLanguages(''); setExperienceYears(''); setVerification('unverified'); setPublicProfile(false); setBio(''); setCertifications('');
    setError('');
  };

  const openAdd = () => { reset(); setEditingId(null); setOpen(true); };

  const openEditContact = (c: CoreContact) => {
    reset();
    setContactType(c.contact_type || 'person');
    setRoleType(c.role_type || 'client');
    setFirstName(c.first_name || '');
    setLastName(c.last_name || '');
    setCompanyName(c.company_name || '');
    setJobTitle(c.job_title || '');
    setEmail(c.email || '');
    setPhone(c.phone || '');
    setWhatsapp(c.whatsapp || '');
    setNotes(c.notes || '');
    setEditingId(c.id);
    setOpen(true);
  };

  const openEditCustomer = (c: CoreCustomer) => {
    reset();
    setCustName(c.display_name || '');
    setCustType(c.customer_type || 'individual');
    setCustCompany(c.company_name || '');
    setCustEmail(c.email || '');
    setCustPhone(c.phone || '');
    setCustNotes(c.notes || '');
    setEditingId(c.id);
    setOpen(true);
  };

  const openEditProfessional = (p: CoreProfessional) => {
    reset();
    setProName(p.display_name || '');
    setProfession(p.profession || '');
    setSpecialties(p.specialties || '');
    setLanguages(p.languages || '');
    setExperienceYears(p.experience_years != null ? String(p.experience_years) : '');
    setVerification(p.verification_status || 'unverified');
    setPublicProfile(!!p.public_profile);
    setBio(p.bio || '');
    setCertifications(p.certifications || '');
    setEditingId(p.id);
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      if (sub === 'contacts') {
        if (!firstName.trim() && !lastName.trim() && !companyName.trim()) { setError('Renseignez au moins un nom.'); setSaving(false); return; }
        const payload = {
          tenantId: org.tenant_id, organizationId: org.id, contactType, roleType,
          firstName, lastName, companyName, jobTitle, email, phone, whatsapp, notes,
        };
        if (editingId) await updateCoreContact(editingId, payload);
        else await createCoreContact(payload);
      } else if (sub === 'customers') {
        if (!custName.trim()) { setError('Le nom du client est obligatoire.'); setSaving(false); return; }
        const payload = {
          tenantId: org.tenant_id, organizationId: org.id, displayName: custName.trim(),
          companyName: custCompany, email: custEmail, phone: custPhone, customerType: custType, notes: custNotes,
        };
        let customerId = editingId;
        if (editingId) {
          await updateCoreCustomer(editingId, payload);
        } else {
          customerId = await createCoreCustomer(payload);
        }
        // Événement du socle : le client est créé/modifié → re-index auto + consommateurs.
        if (customerId) {
          await emitCoreActivity({
            commerceId: org.legacy_user_id ?? null,
            eventKey: editingId ? 'customer.updated' : 'customer.created',
            entityType: 'customer',
            entityId: customerId,
            title: `${editingId ? 'Client modifié' : 'Client créé'} — ${custName.trim()}`,
            description: custEmail || custPhone || null,
            payload: { name: custName.trim(), email: custEmail || null, phone: custPhone || null },
            source: 'socle',
          });
        }
      } else {
        if (!proName.trim()) { setError('Le nom du professionnel est obligatoire.'); setSaving(false); return; }
        const payload = {
          tenantId: org.tenant_id, organizationId: org.id, displayName: proName.trim(),
          profession, specialties, languages, experienceYears: experienceYears ? Number(experienceYears) : null,
          bio, certifications, verificationStatus: verification, publicProfile,
        };
        if (editingId) await updateCoreProfessional(editingId, payload);
        else await createCoreProfessional(payload);
      }
      setOpen(false);
      setEditingId(null);
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Enregistrement impossible.');
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (table: CorePersonTable, id: number, current: string) => {
    setBusyId(id);
    try {
      await setCorePersonStatus(table, id, current === 'inactive' ? 'active' : 'inactive');
      onChanged();
    } finally {
      setBusyId(null);
    }
  };

  const filterByStatus = <T extends { status: string }>(list: T[]): T[] => {
    if (statusFilter === 'all') return list;
    if (statusFilter === 'active') return list.filter((x) => x.status !== 'inactive');
    return list.filter((x) => x.status === 'inactive');
  };

  const subTabs: { key: SubTab; label: string; icon: string; count: number }[] = [
    { key: 'contacts', label: 'Contacts', icon: 'ri-contacts-book-2-line', count: contacts.length },
    { key: 'customers', label: 'Clients', icon: 'ri-user-star-line', count: customers.length },
    { key: 'professionals', label: 'Professionnels', icon: 'ri-briefcase-4-line', count: professionals.length },
  ];

  const CardActions = ({ table, id, status, onEdit }: { table: CorePersonTable; id: number; status: string; onEdit: () => void }) => (
    <div className="flex items-center gap-1.5">
      <button
        onClick={onEdit}
        className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-background-200/70 transition-colors cursor-pointer"
        title="Modifier"
      >
        <i className="ri-edit-line text-sm"></i>
      </button>
      <button
        onClick={() => toggleStatus(table, id, status)}
        disabled={busyId === id}
        className={`w-8 h-8 flex items-center justify-center rounded-full transition-colors cursor-pointer disabled:opacity-50 ${status === 'inactive' ? 'bg-accent-100 text-accent-700 hover:bg-accent-200/70' : 'bg-background-100 text-foreground-600 hover:bg-background-200/70'}`}
        title={status === 'inactive' ? 'Réactiver' : 'Désactiver'}
      >
        <i className={status === 'inactive' ? 'ri-toggle-line text-base' : 'ri-forbid-2-line text-sm'}></i>
      </button>
    </div>
  );

  const contactList = filterByStatus(contacts);
  const customerList = filterByStatus(customers);
  const professionalList = filterByStatus(professionals);

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 overflow-x-auto">
          {subTabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setSub(t.key)}
              className={`flex items-center gap-2 px-3.5 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${sub === t.key ? 'bg-background-50 text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}
            >
              <i className={t.icon}></i>
              {t.label}
              <span className="text-[10px] px-1.5 rounded-full bg-background-200/70 text-foreground-600">{t.count}</span>
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            className="px-3 py-2 rounded-full border border-background-200/70 bg-background-50 text-sm text-foreground-700 focus:outline-none focus:ring-2 focus:ring-primary-300 cursor-pointer"
          >
            <option value="all">Tous les statuts</option>
            <option value="active">Actifs</option>
            <option value="inactive">Désactivés</option>
          </select>
          <button
            onClick={openAdd}
            className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
          >
            <i className="ri-add-line"></i>
            Ajouter
          </button>
        </div>
      </div>

      {sub === 'contacts' && (
        contactList.length === 0 ? <EmptyState icon="ri-contacts-book-2-line" text="Aucun contact." /> : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {contactList.map((c) => {
              const name = c.contact_type === 'company' ? c.company_name : [c.first_name, c.last_name].filter(Boolean).join(' ');
              const roleLabel = ROLE_OPTIONS.find((r) => r.value === c.role_type)?.label || c.role_type;
              const inactive = c.status === 'inactive';
              return (
                <div key={c.id} className={`bg-background-50 border border-background-200/70 rounded-lg p-5 ${inactive ? 'opacity-60' : ''}`}>
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center flex-shrink-0">
                        <i className={c.contact_type === 'company' ? 'ri-building-line' : 'ri-user-line'}></i>
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-foreground-950 truncate">{name || '—'}</div>
                        <div className="text-[11px] text-foreground-400 truncate">{c.job_title || (c.contact_type === 'company' ? 'Entreprise' : 'Personne')}</div>
                      </div>
                    </div>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-background-100 text-foreground-600 font-medium whitespace-nowrap">{roleLabel}</span>
                  </div>
                  <div className="text-xs text-foreground-500 space-y-1 mb-3">
                    <div className="flex items-center gap-1.5"><i className="ri-mail-line text-foreground-400"></i><span className="truncate">{c.email || '—'}</span></div>
                    <div className="flex items-center gap-1.5"><i className="ri-phone-line text-foreground-400"></i><span className="truncate">{c.phone || '—'}</span></div>
                  </div>
                  {(supplierSet.has(c.id) || partnerSet.has(c.id)) && (
                    <div className="flex flex-wrap items-center gap-1.5 mb-3">
                      {supplierSet.has(c.id) && <span className="text-[10px] px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900 font-medium whitespace-nowrap"><i className="ri-truck-line mr-1"></i>Fournisseur</span>}
                      {partnerSet.has(c.id) && <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary-100 text-primary-900 font-medium whitespace-nowrap"><i className="ri-links-line mr-1"></i>Partenaire</span>}
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <StatusBadge status={c.status} />
                    <CardActions table="core_contacts" id={c.id} status={c.status} onEdit={() => openEditContact(c)} />
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}

      {sub === 'customers' && (
        customerList.length === 0 ? <EmptyState icon="ri-user-star-line" text="Aucun client." /> : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {customerList.map((c) => {
              const inactive = c.status === 'inactive';
              const linkedCredit = creditSet.has(c.id);
              const orderCount = orderCounts[c.id] || 0;
              return (
                <div key={c.id} className={`bg-background-50 border border-background-200/70 rounded-lg p-5 ${inactive ? 'opacity-60' : ''}`}>
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-full bg-accent-100 text-accent-700 flex items-center justify-center flex-shrink-0">
                        <i className="ri-user-3-line"></i>
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-foreground-950 truncate">{c.display_name || c.company_name || '—'}</div>
                        <div className="text-[11px] text-foreground-400">{c.customer_type === 'company' ? 'Entreprise' : 'Particulier'}</div>
                      </div>
                    </div>
                    <StatusBadge status={c.status} />
                  </div>
                  <div className="text-xs text-foreground-500 space-y-1 mb-3">
                    <div className="flex items-center gap-1.5"><i className="ri-mail-line text-foreground-400"></i><span className="truncate">{c.email || '—'}</span></div>
                    <div className="flex items-center gap-1.5"><i className="ri-phone-line text-foreground-400"></i><span className="truncate">{c.phone || '—'}</span></div>
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5 mb-3">
                    {c.user_id && <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary-100 text-primary-900 font-medium whitespace-nowrap"><i className="ri-link-m mr-1"></i>Compte Zifek</span>}
                    {linkedCredit && <span className="text-[10px] px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900 font-medium whitespace-nowrap"><i className="ri-book-2-line mr-1"></i>Carnet crédit</span>}
                    {orderCount > 0 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-accent-100 text-accent-900 font-medium whitespace-nowrap"><i className="ri-shopping-bag-3-line mr-1"></i>{orderCount} commande{orderCount > 1 ? 's' : ''}</span>}
                  </div>
                  <div className="flex items-center justify-end">
                    <CardActions table="core_customers" id={c.id} status={c.status} onEdit={() => openEditCustomer(c)} />
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}

      {sub === 'professionals' && (
        professionalList.length === 0 ? <EmptyState icon="ri-briefcase-4-line" text="Aucun professionnel." /> : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {professionalList.map((p) => {
              const inactive = p.status === 'inactive';
              return (
                <div key={p.id} className={`bg-background-50 border border-background-200/70 rounded-lg p-5 ${inactive ? 'opacity-60' : ''}`}>
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center flex-shrink-0">
                        <i className="ri-briefcase-4-line"></i>
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-foreground-950 truncate">{p.display_name || '—'}</div>
                        <div className="text-[11px] text-foreground-400 truncate">{p.profession || 'Profession'}</div>
                      </div>
                    </div>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${p.verification_status === 'verified' ? 'bg-accent-100 text-accent-900' : p.verification_status === 'pending' ? 'bg-secondary-100 text-secondary-900' : 'bg-foreground-100 text-foreground-600'}`}>
                      {p.verification_status === 'verified' ? 'Vérifié' : p.verification_status === 'pending' ? 'En attente' : 'Non vérifié'}
                    </span>
                  </div>
                  <div className="text-xs text-foreground-500 space-y-1 mb-3">
                    <div className="flex items-center gap-1.5"><i className="ri-price-tag-3-line text-foreground-400"></i><span className="truncate">{p.specialties || '—'}</span></div>
                    <div className="flex items-center gap-1.5"><i className="ri-translate-2 text-foreground-400"></i><span className="truncate">{p.languages || '—'}</span></div>
                  </div>
                  <div className="flex items-center justify-between">
                    <StatusBadge status={p.status} />
                    <CardActions table="core_professionals" id={p.id} status={p.status} onEdit={() => openEditProfessional(p)} />
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}

      {open && (
        <Modal
          title={
            editingId
              ? (sub === 'contacts' ? 'Modifier le contact' : sub === 'customers' ? 'Modifier le client' : 'Modifier le professionnel')
              : (sub === 'contacts' ? 'Nouveau contact' : sub === 'customers' ? 'Nouveau client' : 'Nouveau professionnel')
          }
          onClose={() => setOpen(false)}
          footer={
            <>
              <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-full text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Annuler</button>
              <button
                onClick={save}
                disabled={saving}
                className="px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>}
                {editingId ? 'Mettre à jour' : 'Enregistrer'}
              </button>
            </>
          }
        >
          {error && <div className="mb-3 p-2.5 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">{error}</div>}

          {sub === 'contacts' && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Type">
                  <select className={INPUT} value={contactType} onChange={(e) => setContactType(e.target.value)}>
                    <option value="person">Personne</option>
                    <option value="company">Entreprise</option>
                  </select>
                </Field>
                <Field label="Rôle">
                  <select className={INPUT} value={roleType} onChange={(e) => setRoleType(e.target.value)}>
                    {ROLE_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </Field>
              </div>
              {contactType === 'company' ? (
                <Field label="Nom de l’entreprise"><input className={INPUT} value={companyName} onChange={(e) => setCompanyName(e.target.value)} /></Field>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Prénom"><input className={INPUT} value={firstName} onChange={(e) => setFirstName(e.target.value)} /></Field>
                  <Field label="Nom"><input className={INPUT} value={lastName} onChange={(e) => setLastName(e.target.value)} /></Field>
                </div>
              )}
              <Field label="Fonction"><input className={INPUT} value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} placeholder="Ex : Directeur" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Email"><input className={INPUT} value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
                <Field label="Téléphone"><input className={INPUT} value={phone} onChange={(e) => setPhone(e.target.value)} /></Field>
              </div>
              <Field label="WhatsApp"><input className={INPUT} value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} /></Field>
              <Field label="Notes"><textarea className={`${INPUT} min-h-[70px]`} value={notes} onChange={(e) => setNotes(e.target.value)} /></Field>
            </div>
          )}

          {sub === 'customers' && (
            <div className="space-y-3">
              <Field label="Nom du client *"><input className={INPUT} value={custName} onChange={(e) => setCustName(e.target.value)} /></Field>
              <Field label="Type">
                <select className={INPUT} value={custType} onChange={(e) => setCustType(e.target.value)}>
                  <option value="individual">Particulier</option>
                  <option value="company">Entreprise</option>
                </select>
              </Field>
              {custType === 'company' && (
                <Field label="Raison sociale"><input className={INPUT} value={custCompany} onChange={(e) => setCustCompany(e.target.value)} /></Field>
              )}
              <div className="grid grid-cols-2 gap-3">
                <Field label="Email"><input className={INPUT} value={custEmail} onChange={(e) => setCustEmail(e.target.value)} /></Field>
                <Field label="Téléphone"><input className={INPUT} value={custPhone} onChange={(e) => setCustPhone(e.target.value)} /></Field>
              </div>
              <Field label="Notes"><textarea className={`${INPUT} min-h-[70px]`} value={custNotes} onChange={(e) => setCustNotes(e.target.value)} /></Field>
            </div>
          )}

          {sub === 'professionals' && (
            <div className="space-y-3">
              <Field label="Nom du professionnel *"><input className={INPUT} value={proName} onChange={(e) => setProName(e.target.value)} /></Field>
              <Field label="Profession"><input className={INPUT} value={profession} onChange={(e) => setProfession(e.target.value)} placeholder="Ex : Architecte, Dentiste…" /></Field>
              <Field label="Spécialités"><input className={INPUT} value={specialties} onChange={(e) => setSpecialties(e.target.value)} placeholder="Séparées par des virgules" /></Field>
              <Field label="Certifications"><input className={INPUT} value={certifications} onChange={(e) => setCertifications(e.target.value)} placeholder="Facultatif" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Langues"><input className={INPUT} value={languages} onChange={(e) => setLanguages(e.target.value)} placeholder="FR, AR, EN" /></Field>
                <Field label="Années d’expérience"><input type="number" className={INPUT} value={experienceYears} onChange={(e) => setExperienceYears(e.target.value)} /></Field>
              </div>
              <Field label="Statut de vérification">
                <select className={INPUT} value={verification} onChange={(e) => setVerification(e.target.value)}>
                  <option value="unverified">Non vérifié</option>
                  <option value="pending">En attente</option>
                  <option value="verified">Vérifié</option>
                </select>
              </Field>
              <Field label="Présentation"><textarea className={`${INPUT} min-h-[70px]`} value={bio} onChange={(e) => setBio(e.target.value)} /></Field>
              <label className="flex items-center gap-2.5 cursor-pointer">
                <input type="checkbox" checked={publicProfile} onChange={(e) => setPublicProfile(e.target.checked)} className="w-4 h-4 rounded accent-primary-500" />
                <span className="text-sm text-foreground-700">Afficher le profil publiquement</span>
              </label>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}